/**
 * transformPythonError
 *
 * Converts a raw Pyodide/Python error string into a kid-friendly message
 * for K–Grade 10 students on beGalileo. A dimmed technical detail is
 * appended so teachers can still diagnose the root cause.
 *
 * All student-visible errors MUST pass through this function.
 * Do not call it on timeout/stop messages — those are already friendly.
 *
 * Line-number offset: transformCode() wraps student code in
 *   async def __student_main__():
 *       <student line 1>   ← Python reports as line 2
 *       <student line 2>   ← Python reports as line 3
 * so we subtract 1 from every reported file-line to get the student line.
 */
export function transformPythonError(rawError) {
    if (!rawError) return rawError;
    const raw = rawError.toString();

    // Pass through strings that aren't Python errors (e.g. "Pyodide not ready yet")
    if (!/Error|Traceback/i.test(raw)) return raw;

    // ── Extract line number ────────────────────────────────────────────────
    // Match all `File "...", line N` occurrences; the last one is the
    // innermost (student-code) frame.
    const frameMatches = [...raw.matchAll(/File\s+"[^"]*",\s*line\s+(\d+)/g)];
    let lineNum = null;
    if (frameMatches.length > 0) {
        const reported = parseInt(frameMatches[frameMatches.length - 1][1], 10);
        lineNum = Math.max(1, reported - 1); // subtract wrapper offset
    }
    const line = lineNum ? `line ${lineNum}` : "your code";

    // ── Technical detail for teachers ─────────────────────────────────────
    // Use the last non-empty line of the traceback (the exception type + msg).
    const nonEmpty = raw.split('\n').map(l => l.trim()).filter(Boolean);
    const techDetail = nonEmpty[nonEmpty.length - 1] || raw.trim();

    function msg(friendly) {
        return `${friendly}\n\n(Technical detail: ${techDetail})`;
    }

    // ════════════════════════════════════════════════════════════════════════
    // SYNTAX & STRUCTURE ERRORS
    // ════════════════════════════════════════════════════════════════════════

    if (/SyntaxError.*invalid syntax/i.test(raw)) {
        return msg(
            `There's a typo or missing punctuation near ${line}. ` +
            `Check for missing \`:\` after \`if\`, \`for\`, \`while\`, or \`def\`.`
        );
    }
    if (/SyntaxError.*EOL while scanning string literal/i.test(raw)) {
        return msg(
            `You opened a quote but didn't close it on ${line}. Check your ' or " marks.`
        );
    }
    if (/SyntaxError.*(unexpected EOF|EOF while parsing)/i.test(raw)) {
        return msg(
            `Looks like you opened a bracket \`(\` or \`[\` but never closed it. Check ${line}.`
        );
    }
    if (/IndentationError.*expected an indented block/i.test(raw)) {
        return msg(
            `The code inside your \`if\` or \`for\` block needs to be indented ` +
            `(add spaces at the start of ${line}).`
        );
    }
    if (/IndentationError.*unexpected indent/i.test(raw)) {
        const cap = line.charAt(0).toUpperCase() + line.slice(1);
        return msg(
            `${cap} has extra spaces at the start that don't belong there. Remove the indentation.`
        );
    }
    if (/TabError/i.test(raw)) {
        return msg(
            `You're mixing tabs and spaces on ${line}. Use only spaces — 4 spaces per indent level.`
        );
    }

    // ════════════════════════════════════════════════════════════════════════
    // NAME & VARIABLE ERRORS
    // ════════════════════════════════════════════════════════════════════════

    const nameMatch = raw.match(/NameError[^']*'(.+?)' is not defined/i);
    if (nameMatch) {
        const name = nameMatch[1];
        if (name === 'true') {
            return msg(`Python is case-sensitive — use \`True\` with a capital T, not \`true\`.`);
        }
        if (name === 'false') {
            return msg(`Python is case-sensitive — use \`False\` with a capital F, not \`false\`.`);
        }
        if (name === 'null') {
            return msg(`In Python, empty/nothing is called \`None\` with a capital N, not \`null\`.`);
        }
        // Catch obvious misspellings of `print` (primt, prrint, prant, prnt, etc.)
        if (name !== 'print' && /^pr[a-z]{0,3}t$/i.test(name)) {
            return msg(`Looks like \`${name}\` isn't a Python command — did you mean \`print\`?`);
        }
        return msg(
            `You used \`${name}\` on ${line} but never created it. ` +
            `Try adding \`${name} = something\` before using it.`
        );
    }

    // ════════════════════════════════════════════════════════════════════════
    // TYPE ERRORS
    // ════════════════════════════════════════════════════════════════════════

    if (/TypeError.*can only concatenate str.*to str/i.test(raw)) {
        return msg(
            `You can't mix text and numbers directly. ` +
            `Try wrapping the number in \`str()\` — like \`str(age)\`.`
        );
    }
    if (/TypeError.*unsupported operand type.*for \+/i.test(raw) &&
        /(int.*str|str.*int)/i.test(raw)) {
        return msg(
            `You're trying to add a number and some text together. ` +
            `Convert one of them first — use \`int()\` or \`str()\`.`
        );
    }
    if (/TypeError.*'int' object is not iterable/i.test(raw)) {
        return msg(`You can't loop over a number directly. Try \`for i in range(X):\` instead.`);
    }
    if (/TypeError.*'str' object is not callable/i.test(raw)) {
        return msg(
            `Looks like you're trying to use it as a function but it's actually text. Check ${line}.`
        );
    }
    const argsMatch = raw.match(/TypeError.*takes (\d+) positional argument.*but (\d+) w/i);
    if (argsMatch) {
        const expected = argsMatch[1];
        const got = argsMatch[2];
        return msg(
            `Your function expects ${expected} input${expected === '1' ? '' : 's'} but you gave it ${got}. ` +
            `Check how many values you're passing in.`
        );
    }
    if (/TypeError.*'NoneType' object is not subscriptable/i.test(raw)) {
        return msg(
            `The variable on ${line} is empty (None) — it doesn't have any items to look up.`
        );
    }

    // ════════════════════════════════════════════════════════════════════════
    // VALUE ERRORS
    // ════════════════════════════════════════════════════════════════════════

    if (/ValueError.*invalid literal for int\(\) with base 10/i.test(raw)) {
        return msg(
            `You're trying to turn text into a number but it doesn't look like a number. ` +
            `Check what you're passing to \`int()\`.`
        );
    }
    if (/ValueError.*(x not in list|list\.remove)/i.test(raw)) {
        return msg(
            `You're trying to remove something that isn't in the list. Check if it's spelled correctly.`
        );
    }
    if (/ValueError.*too many values to unpack/i.test(raw)) {
        return msg(
            `You have more items than variables to put them in on ${line}. ` +
            `Make sure the number of variables matches.`
        );
    }
    if (/ValueError.*not enough values to unpack/i.test(raw)) {
        return msg(
            `You have fewer items than variables on ${line}. ` +
            `Make sure the number of variables matches.`
        );
    }

    // ════════════════════════════════════════════════════════════════════════
    // INDEX & KEY ERRORS
    // ════════════════════════════════════════════════════════════════════════

    if (/IndexError.*list index out of range/i.test(raw)) {
        return msg(
            `You're trying to get an item from your list, but that position doesn't exist. ` +
            `Remember lists start counting from 0!`
        );
    }
    if (/KeyError/i.test(raw)) {
        const keyMatch = raw.match(/KeyError:\s*(.+)$/m);
        const key = keyMatch ? keyMatch[1].replace(/^['"]|['"]$/g, '').trim() : "that key";
        return msg(
            `Your dictionary doesn't have a key called \`${key}\`. ` +
            `Check your spelling or use \`.get()\` to be safe.`
        );
    }

    // ════════════════════════════════════════════════════════════════════════
    // MATH ERRORS
    // ════════════════════════════════════════════════════════════════════════

    if (/ZeroDivisionError/i.test(raw)) {
        return msg(
            `You're trying to divide by zero on ${line} — that's not allowed in math either! ` +
            `Check your denominator.`
        );
    }
    if (/OverflowError/i.test(raw)) {
        return msg(
            `The number you're working with is too big for Python to handle. Try a smaller value.`
        );
    }

    // ════════════════════════════════════════════════════════════════════════
    // ATTRIBUTE ERRORS
    // ════════════════════════════════════════════════════════════════════════

    if (/AttributeError.*'str' object has no attribute 'append'/i.test(raw)) {
        return msg(
            `\`append\` is for lists, not text. If you want to add to text, use \`+\` instead.`
        );
    }
    if (/AttributeError.*'list' object has no attribute 'keys'/i.test(raw)) {
        return msg(
            `\`.keys()\` is for dictionaries, not lists. Check what type of variable you're working with.`
        );
    }
    if (/AttributeError.*'NoneType' object has no attribute/i.test(raw)) {
        return msg(
            `The variable on ${line} is empty (None) — it doesn't have any methods you can call on it.`
        );
    }

    // ════════════════════════════════════════════════════════════════════════
    // IMPORT ERRORS
    // ════════════════════════════════════════════════════════════════════════

    if (/ModuleNotFoundError/i.test(raw)) {
        const modMatch =
            raw.match(/No module named '(.+?)'/i) ||
            raw.match(/ModuleNotFoundError[^']*'(.+?)'/i);
        const modName = modMatch ? modMatch[1] : "that package";
        return msg(
            `The package \`${modName}\` isn't available in this editor. ` +
            `You can use: \`math\`, \`random\`, \`turtle\`, \`time\`, \`datetime\`.`
        );
    }
    if (/ImportError/i.test(raw)) {
        return msg(
            `Something in your \`import\` on ${line} doesn't exist. Check the spelling.`
        );
    }

    // ════════════════════════════════════════════════════════════════════════
    // RECURSION
    // ════════════════════════════════════════════════════════════════════════

    if (/RecursionError/i.test(raw)) {
        return msg(
            `Your function keeps calling itself without stopping. ` +
            `Make sure you have a condition that ends the recursion.`
        );
    }

    // ════════════════════════════════════════════════════════════════════════
    // CATCH-ALL
    // ════════════════════════════════════════════════════════════════════════

    return msg(
        `Something went wrong on ${line}. Read the message carefully — it often tells you ` +
        `exactly what to fix. If you're stuck, ask your teacher!`
    );
}